// ----- Phase 5.X-lifecycle-report-panel — LifecycleReportPanel -----
//
// Modal panel showing the audit lifecycle history for a submission.
// Triggered from the submission row's "History" button on the Dashboard.
// Matches V1's LifecycleReportPanel.jsx layout:
//   - Fixed overlay + centered modal card
//   - Events in reverse-chronological order
//   - Action icon, description, actor, timestamp
//   - Before/after data as collapsible inline summary
//
// Data source: GET /api/v1/submissions/:id/lifecycle
// (AuditLog rows for the submission + its documents, newest-first, max 100)
//
// V1 reference: /Users/victorramsaywak/autoectd/src/components/viewers/LifecycleReportPanel.jsx

import {
  useCallback,
  useEffect,
  useState,
  type CSSProperties,
  type MouseEvent,
  type ReactElement,
} from 'react';
import { v2, type LifecycleEvent } from '@/api/v2Client';

export interface LifecycleReportPanelProps {
  /** The submission to show history for. */
  readonly submissionId: string;
  /** Human-readable label shown in the modal header (e.g. drug name + seq). */
  readonly submissionLabel: string;
  /** Called when the user closes the panel. */
  readonly onClose: () => void;
}

// ---- Styles (matches V1's modal visual treatment) ----

const overlayStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0,0,0,0.5)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const cardStyle: CSSProperties = {
  background: 'var(--surface)',
  borderRadius: 12,
  padding: 24,
  maxWidth: 800,
  width: '95%',
  maxHeight: '85vh',
  overflowY: 'auto',
  boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
};

const summaryBarStyle: CSSProperties = {
  display: 'flex',
  gap: 16,
  marginBottom: 16,
  padding: '10px 14px',
  background: 'var(--surface2)',
  borderRadius: 8,
  fontSize: 13,
};

const eventRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '8px 0',
  borderBottom: '1px solid var(--border)',
  fontSize: 13,
};

const actionBadgeBase: CSSProperties = {
  fontSize: 10,
  padding: '2px 7px',
  borderRadius: 4,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

// ---- Helpers ----

/** Icon for the event's action category. */
function actionIcon(action: string): string {
  if (action.includes('lifecycle')) return '🔄';
  if (action.includes('create') || action.includes('upload')) return '📄';
  if (action.includes('delete')) return '🗑️';
  if (action.includes('update') || action.includes('patch')) return '✏️';
  if (action.includes('reorder')) return '↕️';
  if (action.includes('validate') || action.includes('validation')) return '✅';
  if (action.includes('compile') || action.includes('publish')) return '📦';
  if (action.includes('confirm') || action.includes('approve')) return '✔️';
  return '📋';
}

function actionBadgeStyle(action: string): CSSProperties {
  if (action.includes('lifecycle'))
    return { ...actionBadgeBase, background: '#EFF6FF', color: '#1d4ed8' };
  if (action.includes('delete'))
    return { ...actionBadgeBase, background: 'var(--red-bg, #FEF2F2)', color: 'var(--red, #b91c1c)' };
  if (action.includes('create') || action.includes('upload'))
    return { ...actionBadgeBase, background: 'var(--green-bg, #F0FFF4)', color: 'var(--green, #15803d)' };
  return {
    ...actionBadgeBase,
    background: 'var(--surface2)',
    color: 'var(--text-muted)',
  };
}

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/** Render a small summary of before/after data if present. */
function DiffSummary({ before, after }: Pick<LifecycleEvent, 'before' | 'after'>): ReactElement | null {
  if (!before && !after) return null;
  const lines: string[] = [];
  const allKeys = new Set([
    ...Object.keys(before ?? {}),
    ...Object.keys(after ?? {}),
  ]);
  for (const key of allKeys) {
    const b = before?.[key];
    const a = after?.[key];
    if (b !== a) {
      lines.push(`${key}: ${String(b ?? '—')} → ${String(a ?? '—')}`);
    }
  }
  if (lines.length === 0) return null;
  return (
    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
      {lines.slice(0, 3).map((l) => (
        <div key={l}>{l}</div>
      ))}
      {lines.length > 3 && <div>…{lines.length - 3} more</div>}
    </div>
  );
}

// ---- Main Component ----

export function LifecycleReportPanel({
  submissionId,
  submissionLabel,
  onClose,
}: LifecycleReportPanelProps): ReactElement {
  const [events, setEvents] = useState<readonly LifecycleEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await v2.submissions.getLifecycleHistory(submissionId);
      setEvents(res.events);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load lifecycle history');
    } finally {
      setLoading(false);
    }
  }, [submissionId]);

  useEffect(() => {
    void fetchHistory();
  }, [fetchHistory]);

  // Close on overlay click (not card click).
  const handleOverlayClick = (e: MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      data-testid="lifecycle-report-overlay"
      style={overlayStyle}
      onClick={handleOverlayClick}
    >
      <div data-testid="lifecycle-report-panel" style={cardStyle} role="dialog" aria-modal="true" aria-label="Lifecycle history">
        {/* Header */}
        <h2 style={{ margin: '0 0 8px 0', fontSize: 18 }}>
          Lifecycle History — {submissionLabel}
        </h2>

        {/* Summary bar */}
        <div style={summaryBarStyle}>
          {loading ? (
            <span style={{ color: 'var(--text-muted)' }}>Loading…</span>
          ) : error ? (
            <span style={{ color: 'var(--red, #b91c1c)' }}>{error}</span>
          ) : (
            <>
              <span data-testid="lifecycle-event-count" style={{ color: 'var(--text-muted)' }}>
                {events.length} event{events.length !== 1 ? 's' : ''}
              </span>
              <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                newest first · capped at 100
              </span>
            </>
          )}
        </div>

        {/* Event list */}
        {!loading && !error && events.length === 0 && (
          <div
            data-testid="lifecycle-empty-state"
            style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)', fontSize: 13 }}
          >
            No lifecycle events recorded for this submission yet.
          </div>
        )}

        {!loading && !error && events.length > 0 && (
          <div data-testid="lifecycle-event-list">
            {events.map((ev) => (
              <div
                key={ev.id}
                data-testid={`lifecycle-event-${ev.id}`}
                style={eventRowStyle}
              >
                {/* Icon */}
                <span style={{ fontSize: 16, flexShrink: 0 }} aria-hidden="true">
                  {actionIcon(ev.action)}
                </span>

                {/* Main content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span data-testid={`lifecycle-action-${ev.id}`} style={actionBadgeStyle(ev.action)}>
                      {ev.action}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {ev.targetType} {ev.targetId.slice(0, 8)}…
                    </span>
                  </div>
                  <DiffSummary before={ev.before} after={ev.after} />
                </div>

                {/* Actor + time */}
                <div style={{ flexShrink: 0, textAlign: 'right', fontSize: 11, color: 'var(--text-muted)' }}>
                  <div data-testid={`lifecycle-actor-${ev.id}`} style={{ fontWeight: 600 }}>
                    {ev.userName}
                  </div>
                  <div data-testid={`lifecycle-timestamp-${ev.id}`}>
                    {formatTimestamp(ev.timestamp)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          <button
            type="button"
            className="btn btn-primary"
            data-testid="lifecycle-close-btn"
            onClick={onClose}
            style={{
              padding: '8px 20px',
              background: 'var(--primary)',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
